import { useEffect } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { TransferFormValues } from '~/components/forms/transfer/transferSchema'
import { SupportedChainId } from '~/constants/chains'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'
import { isVaultToken } from '~/utils/vault'

/**
 * Applies the Earn recipient rule to the transfer form. The token picker offers
 * Earn tokens only while every recipient is one of the org's accounts. If a
 * recipient outside the org is then added, the hook clears the token.
 */
export const useEarnTokenEnforcement = () => {
  const { control, setValue } = useFormContext<TransferFormValues>()
  const recipients = useWatch({ control, name: 'recipients' })
  const transferToken = useWatch({ control, name: 'transferToken' })
  const { smartAccounts, isLoading: isLoadingSmartAccounts } =
    useSmartAccounts()

  const selectedToken = transferToken?.token
  const selectedChainId = transferToken?.chainId
  const isEarnSelected =
    !!selectedToken &&
    !!selectedChainId &&
    Address.validate(selectedToken) &&
    !!isVaultToken(selectedChainId, selectedToken)

  const hasExternalRecipient = (recipients ?? []).some((recipient) => {
    const address = recipient?.receiver?.address
    if (!address || !Address.validate(address)) return false
    return !smartAccounts.some((account) =>
      Address.equal(account.address, address),
    )
  })

  // eslint-disable-next-line local/noUseEffect -- the conflict between recipient and token can come from the picker, a URL prefill, or a new multi-send row
  useEffect(() => {
    // An empty account list means the query is still running, not that every
    // recipient is external.
    if (isLoadingSmartAccounts || !isEarnSelected || !hasExternalRecipient) {
      return
    }

    // Clear per field, and with an empty address rather than undefined:
    // setValue on the parent object leaves the registered inputs showing
    // their old selection, and an undefined field falls back to the form's
    // default, which a prefilled URL sets to this very token. The amount
    // stays. Never write '0x': a truthy token with no chain crashes the
    // consumers that format it.
    setValue('transferToken.token', '' as unknown as IAddress)
    setValue('transferToken.chainId', undefined as unknown as SupportedChainId)
  }, [isLoadingSmartAccounts, isEarnSelected, hasExternalRecipient, setValue])
}
